import type { ReactNode } from 'react'
import { toast } from 'sonner'

import type { ManualStatusCorrectionBody } from '@/api/hooks'
import { ChoicePicker } from '@/components/choice-picker'
import type { StatusChoice } from '@/components/manual-status-options'
import { Badge } from '@/components/ui/badge'
import { formatApiErrorMessage } from '@/lib/api-error'
import { formatStatus } from '@/lib/utils'

/** What project history records for a status picked from its pill. */
export const STATUS_EDIT_REASON = 'Status changed on the dashboard'

interface ManualStatusControlProps {
  entityLabel: string
  currentStatus: string
  statusRevision: number
  statusSource: string
  statuses: StatusChoice[]
  loading: boolean
  onSubmit: (
    body: ManualStatusCorrectionBody,
    callbacks: { onSuccess: () => void; onError: (error: unknown) => void },
  ) => void
  /** The status pill to click; a plain badge of the current status when omitted. */
  trigger?: ReactNode
  /** What the pill says when it is not the stored status, e.g. the PO's Order pill "On hold". */
  triggerLabel?: string
  align?: 'start' | 'center' | 'end'
}

/**
 * Click a status pill to pick a new status from a small menu; picking one saves it (USA-1451).
 * Moving a record backward is allowed and recorded in history like any other change.
 */
export function ManualStatusControl({
  entityLabel,
  currentStatus,
  statusRevision,
  statusSource,
  statuses,
  loading,
  onSubmit,
  trigger,
  triggerLabel,
  align,
}: ManualStatusControlProps) {
  const label = (value: string) => statuses.find((choice) => choice.value === value)?.label ?? formatStatus(value)
  const manuallySet = statusSource === 'manual' || statusSource === 'manual_rollup'
  const entity = entityLabel.charAt(0).toUpperCase() + entityLabel.slice(1)

  const save = (status: string) =>
    onSubmit(
      {
        status,
        reason: STATUS_EDIT_REASON,
        expected_status: currentStatus,
        expected_revision: statusRevision,
        // A pick is the correction: moving backward needs no second confirmation.
        allow_regression: true,
      },
      {
        onSuccess: () => toast.success(`${entity} marked ${label(status)}`),
        onError: (error) => toast.error(formatApiErrorMessage('Status not changed', error)),
      },
    )

  return (
    <span className="inline-flex items-center gap-1.5">
      <ChoicePicker
        title={`${entity} status`}
        label={`${entity} status: ${triggerLabel ?? label(currentStatus)}. Change status`}
        value={currentStatus}
        choices={statuses}
        pending={loading}
        align={align}
        onSelect={save}
      >
        {trigger ?? <Badge variant="outline">{label(currentStatus)}</Badge>}
      </ChoicePicker>
      {manuallySet && (
        <Badge
          variant="outline"
          className="border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-200"
        >
          Manually set
        </Badge>
      )}
    </span>
  )
}
